import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { openAgent } from "../utils/openAgent";

const Footer = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter a valid email address");
      return;
    }
    toast.success("Thank you for subscribing!", {
      description: "We'll keep you updated with the latest insights from Simpel.",
    });
    setEmail("");
  };

  const handleNavClick = (path, hash) => {
    if (path) {
      navigate(path);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => {
          document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
        }, 250);
      }
    }
  };

  return (
    <footer
      className="relative w-full border-t border-[#1E293B]/70 overflow-hidden text-white"
      style={{
        background:
          "radial-gradient(ellipse 100% 70% at 50% 0%, rgba(23, 56, 98, 1) 0%, rgba(2, 17, 28, 1) 65%, #010610 100%)",
      }}
    >
      <div className="mx-auto max-w-[1440px] px-6 pt-16 pb-24 sm:pb-12 lg:px-[70px] lg:pt-20 lg:pb-14">
        {/* Main 4-Column Grid */}
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8 xl:gap-12">
          {/* Column 1: Brand & Logo & Tagline (span 4 cols) */}
          <div className="flex flex-col gap-5 lg:col-span-4">
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center gap-3.5 w-fit group"
            >
              {/* High-res 3D Ribbon Logo Icon Mark */}
              <img
                src="/images/simpel-icon-mark.png"
                alt="Simpel"
                className="h-[46px] sm:h-[52px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              />
              {/* Large, Crisp DOM Text */}
              <div className="flex flex-col justify-center">
                <span className="text-[30px] sm:text-[36px] font-bold leading-none tracking-tight text-white">
                  Simpel
                </span>
                <span className="mt-1 text-[9.5px] sm:text-[11px] font-bold uppercase tracking-[2.8px] sm:tracking-[3.2px] text-[#94A3B8]">
                  AUTOMATION SIMPLIFIED
                </span>
              </div>
            </Link>
            <p className="max-w-[320px] text-[14px] font-normal leading-[24px] text-[#94A3B8]">
              We build AI agents and automation workflows to simplify complex
              business operations.
            </p>
          </div>

          {/* Column 2: COMPANY (span 2 cols) */}
          <div className="flex flex-col gap-4 lg:col-span-2">
            <h4 className="text-[12px] font-bold uppercase tracking-[2px] text-white">
              COMPANY
            </h4>
            <ul className="flex flex-col gap-3 text-[14px]">
              <li>
                <button
                  type="button"
                  onClick={() => handleNavClick("/about")}
                  className="text-[#94A3B8] transition-colors hover:text-white"
                >
                  About us
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openAgent}
                  className="text-[#94A3B8] transition-colors hover:text-white"
                >
                  Contact Us
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleNavClick("/careers")}
                  className="text-[#94A3B8] transition-colors hover:text-white"
                >
                  Careers
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: LEGAL (span 2 cols) */}
          <div className="flex flex-col gap-4 lg:col-span-2">
            <h4 className="text-[12px] font-bold uppercase tracking-[2px] text-white">
              LEGAL
            </h4>
            <ul className="flex flex-col gap-3 text-[14px]">
              <li>
                <button
                  type="button"
                  onClick={() => handleNavClick("/privacy-policy")}
                  className="text-[#94A3B8] transition-colors hover:text-white cursor-pointer"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleNavClick("/terms-of-use")}
                  className="text-[#94A3B8] transition-colors hover:text-white cursor-pointer"
                >
                  Terms of Use
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleNavClick("/cookie-policy")}
                  className="text-[#94A3B8] transition-colors hover:text-white cursor-pointer"
                >
                  Cookie Policy
                </button>
              </li>
            </ul>
          </div>

          {/* Column 4: STAY UPDATED & Email Newsletter (span 4 cols) */}
          <div className="flex flex-col gap-4 lg:col-span-4">
            <h4 className="text-[12px] font-bold uppercase tracking-[2px] text-[#38BDF8]">
              STAY UPDATED
            </h4>
            <p className="max-w-[340px] text-[13.5px] font-normal leading-[22px] text-[#94A3B8]">
              Get the latest insights, ideas and opportunities from Simpel.
            </p>

            {/* Email Subscription Capsule */}
            <form
              onSubmit={handleSubscribe}
              className="mt-1 flex w-full max-w-[380px] items-center rounded-full border border-[#0284C7]/50 bg-[#020B18]/90 pl-4 pr-1.5 py-1.5 shadow-[0_0_20px_rgba(2,132,199,0.15)] transition-all duration-300 focus-within:border-[#05D6DA] focus-within:shadow-[0_0_25px_rgba(5,214,218,0.25)] focus-within:ring-1 focus-within:ring-[#05D6DA]"
            >
              <Mail className="h-4 w-4 shrink-0 text-[#94A3B8] mr-2.5" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full bg-transparent text-[13.5px] text-white placeholder-[#64748B] outline-none"
              />
              <button
                type="submit"
                aria-label="Subscribe"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-[#06B6D4] to-[#2563EB] text-white shadow-[0_0_15px_rgba(6,182,212,0.4)] transition-transform duration-200 hover:scale-105 active:scale-95"
              >
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar: Copyright & Social Links */}
        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
          <p className="text-[13px] font-normal text-[#94A3B8]">
            © {new Date().getFullYear()} Simpel. All rights reserved.
          </p>

          {/* Social Icons (LinkedIn, YouTube, X) with clearance for floating chat widget */}
          <div className="flex items-center gap-3 sm:mr-48 lg:mr-56">
            {/* LinkedIn */}
            <a
              href="https://www.linkedin.com/company/simpeltechlabs"
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn - Simpel Techlabs Pvt. Ltd."
              title="Simpel Techlabs Pvt. Ltd. on LinkedIn"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-[#94A3B8] transition-all duration-200 hover:border-white/50 hover:bg-white/10 hover:text-white active:scale-95"
            >
              <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24">
                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v7.6h2.8v-7.6h-2.8M7.86 6.5a1.63 1.63 0 0 0-1.63 1.63c0 .9.73 1.63 1.63 1.63.9 0 1.63-.73 1.63-1.63 0-.9-.73-1.63-1.63-1.63z" />
              </svg>
            </a>

            {/* YouTube */}
            <a
              href="https://www.youtube.com/@SimpelTechlabsPrivateLimited"
              target="_blank"
              rel="noreferrer"
              aria-label="YouTube - Simpel Techlabs Private Limited"
              title="Simpel Techlabs Private Limited on YouTube"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-[#94A3B8] transition-all duration-200 hover:border-white/50 hover:bg-white/10 hover:text-white active:scale-95"
            >
              <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24">
                <path d="M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22 1.1.28 1.9.07.8.1 1.49.1 2.09L22 12c0 2.19-.16 3.8-.44 4.83-.25.9-.83 1.48-1.73 1.73-.47.13-1.33.22-2.65.28-1.3.07-2.49.1-3.59.1L12 19c-4.19 0-6.8-.16-7.83-.44-.9-.25-1.48-.83-1.73-1.73-.13-.47-.22-1.1-.28-1.9-.07-.8-.1-1.49-.1-2.09L2 12c0-2.19.16-3.8.44-4.83.25-.9.83-1.48 1.73-1.73.47-.13 1.33-.22 2.65-.28 1.3-.07 2.49-.1 3.59-.1L12 5c4.19 0 6.8.16 7.83.44.9.25 1.48.83 1.73 1.73z" />
              </svg>
            </a>

            {/* X (Twitter) */}
            <a
              href="https://x.com/search?q=Simpel+Techlabs"
              target="_blank"
              rel="noreferrer"
              aria-label="X (Twitter) - Simpel Techlabs"
              title="Simpel Techlabs on X"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-[#94A3B8] transition-all duration-200 hover:border-white/50 hover:bg-white/10 hover:text-white active:scale-95"
            >
              <svg className="h-3.5 w-3.5 fill-current" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;